import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Drawer,
  FormControlLabel,
  CircularProgress,
  IconButton,
  LinearProgress,
  Radio,
  Typography,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SyncIcon from '@mui/icons-material/Sync';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { syncRates } from '../../services/api';
import useChannelMappingRows from '../../hooks/useChannelMappingRows';
import { compareRatePlans } from '../../utils/ratePlanSort';
import AllChannelsSelector, {
  selectionTargetsOtas,
} from '../common/AllChannelsSelector';
import {
  createDrawerOverlayProps,
  getDrawerRootSx,
} from '../../utils/drawerOverlayProps';
import {
  getBookingDrawerFooterSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPaperSx,
  getBookingDrawerPrimaryButtonSx,
} from '../../utils/bookingDrawerLayout';
import RateActionSuccessDialog, { RATE_SUCCESS_COPY } from './RateActionSuccessDialog';

const THEME_COLOR = '#4ce1b6';

const DAY_OPTIONS = [
  { value: 0, label: 'Sun', shortLabel: 'S', color: '#FF6B6B' },
  { value: 1, label: 'Mon', shortLabel: 'M', color: '#4ECDC4' },
  { value: 2, label: 'Tue', shortLabel: 'T', color: '#45B7D1' },
  { value: 3, label: 'Wed', shortLabel: 'W', color: '#FFA07A' },
  { value: 4, label: 'Thu', shortLabel: 'T', color: '#98D8C8' },
  { value: 5, label: 'Fri', shortLabel: 'F', color: '#F7DC6F' },
  { value: 6, label: 'Sat', shortLabel: 'S', color: '#BB8FCE' },
];

const rowKey = (roomTypeId, ratePlanId) => `${roomTypeId}:${ratePlanId}`;

/**
 * Sync Rates drawer — push stored rates to OTAs in one update-rates call.
 * Different nightly rates become separate updates[] blocks (date ranges coalesced).
 */
export default function RateSyncDrawer({
  open,
  onClose,
  roomTypes: _roomTypes = [],
  ratePlans: _ratePlans = [],
  channels = [],
  isDark,
  themeColors,
  onSaved,
}) {
  const [dateRange, setDateRange] = useState([null, null]);
  const [daySelectionType, setDaySelectionType] = useState('custom');
  const [selectedDays, setSelectedDays] = useState([0, 1, 2, 3, 4, 5, 6]);
  const [selectedChannels, setSelectedChannels] = useState(['all']);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successDialog, setSuccessDialog] = useState(null);
  const drawerRootRef = useRef(null);
  const { mappingPairs, mappingsLoading } = useChannelMappingRows(open);
  const drawerOverlays = useMemo(
    () => createDrawerOverlayProps(isDark, isDark ? themeColors.surface : '#ffffff'),
    [isDark, themeColors.surface]
  );

  const planRows = useMemo(
    () =>
      mappingPairs.map((pair) => ({
        ...pair,
        key: rowKey(pair.roomTypeId, pair.ratePlanId),
      })),
    [mappingPairs]
  );

  const rowsByRoom = useMemo(() => {
    const map = new Map();
    planRows.forEach((row) => {
      if (!map.has(row.roomTypeId)) {
        map.set(row.roomTypeId, { name: row.roomTypeName, rows: [] });
      }
      map.get(row.roomTypeId).rows.push(row);
    });
    map.forEach((group) => {
      group.rows.sort(compareRatePlans);
    });
    return map;
  }, [planRows]);

  const resetForm = useCallback(() => {
    setDateRange([moment().startOf('day'), moment().add(7, 'day').startOf('day')]);
    setDaySelectionType('custom');
    setSelectedDays([0, 1, 2, 3, 4, 5, 6]);
    setSelectedChannels(['all']);
    setErrorMessage(null);
    setSuccessDialog(null);
    setSelectedKeys(new Set(planRows.map((r) => r.key)));
  }, [planRows]);

  useEffect(() => {
    if (open) resetForm();
    // Reset only when the drawer opens, not when mappings refresh after save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || planRows.length === 0) return;
    setSelectedKeys(new Set(planRows.map((row) => row.key)));
  }, [open, planRows]);

  const allSelected = planRows.length > 0 && selectedKeys.size === planRows.length;
  const someSelected = selectedKeys.size > 0 && selectedKeys.size < planRows.length;

  const selectDayType = (type) => {
    setDaySelectionType(type);
    if (type === 'weekdays') setSelectedDays([1, 2, 3, 4, 5]);
    else if (type === 'weekend') setSelectedDays([0, 6]);
    else setSelectedDays([0, 1, 2, 3, 4, 5, 6]);
  };

  const toggleDay = (day) => {
    setSelectedDays((prev) => {
      if (prev.includes(day)) {
        const next = prev.filter((d) => d !== day);
        return next.length ? next : prev;
      }
      return [...prev, day].sort((a, b) => a - b);
    });
  };

  const collectDates = () => {
    if (!dateRange[0] || !dateRange[1]) return [];
    const dates = [];
    let current = moment(dateRange[0]).startOf('day');
    const end = moment(dateRange[1]).startOf('day');
    while (current.isSameOrBefore(end, 'day')) {
      if (selectedDays.includes(current.day())) dates.push(current.format('YYYY-MM-DD'));
      current.add(1, 'day');
    }
    return dates;
  };

  const handleSync = async () => {
    setErrorMessage(null);
    setSuccessDialog(null);

    if (!dateRange[0] || !dateRange[1]) {
      setErrorMessage('Select a date range');
      return;
    }
    if (selectedKeys.size === 0) {
      setErrorMessage('Select at least one rate plan');
      return;
    }
    if (!selectionTargetsOtas(selectedChannels)) {
      setErrorMessage('Turn on All Channels to sync rates to OTAs');
      return;
    }

    const dates = collectDates();
    if (!dates.length) {
      setErrorMessage('No dates match the selected weekdays');
      return;
    }

    const items = planRows
      .filter((r) => selectedKeys.has(r.key))
      .map((r) => ({ room_type_id: r.roomTypeId, rate_plan_id: r.ratePlanId }));

    setIsSaving(true);
    try {
      const result = await syncRates({
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        dates,
        items,
      });
      if (!result?.success) {
        setErrorMessage(result?.error || result?.message || 'Rate sync failed');
        return;
      }
      if (typeof onSaved === 'function') await onSaved();
      setSuccessDialog(RATE_SUCCESS_COPY.syncRates);
    } catch (error) {
      setErrorMessage(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          'Rate sync failed'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => !isSaving && onClose()}
      sx={getDrawerRootSx()}
      PaperProps={{
        sx: getBookingDrawerPaperSx(isDark, {
          width: { xs: '100%', sm: 600 },
          overflow: 'hidden',
        }),
      }}
    >
      <Box
        ref={drawerRootRef}
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            ...getBookingDrawerHeaderSx(isDark),
            position: 'relative',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <SyncIcon sx={{ color: THEME_COLOR, fontSize: 24, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ color: themeColors.text, fontWeight: 600, lineHeight: 1.2 }}>
                Sync Rates
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Push stored rates to OTAs · one sync call
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={isSaving}
            size="small"
            sx={{ color: isDark ? themeColors.subtleText : '#666' }}
          >
            <CloseIcon />
          </IconButton>
          {isSaving && (
            <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2 }} />
          )}
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
          {errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Box
            sx={{
              mb: 2.5,
              p: 1.5,
              borderRadius: 1.5,
              border: `1px solid ${alpha(THEME_COLOR, 0.35)}`,
              backgroundColor: isDark ? alpha(THEME_COLOR, 0.08) : alpha(THEME_COLOR, 0.06),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <SyncIcon sx={{ color: THEME_COLOR, fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Current rates → OTAs
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Reads rates already saved for each date and pushes them to OTAs. Days with
              different prices become separate blocks in the same sync call.
            </Typography>
          </Box>

          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="subtitle2" gutterBottom>
                Date range
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <DatePicker
                  label="From"
                  value={dateRange[0]}
                  minDate={moment().startOf('day')}
                  onChange={(v) => {
                    if (!v) return;
                    const from = v.isBefore(moment().startOf('day'), 'day')
                      ? moment().startOf('day')
                      : v;
                    setDateRange([from, from.clone().add(1, 'day')]);
                  }}
                  slotProps={{
                    ...drawerOverlays.datePickerSlotProps,
                    textField: { size: 'small', fullWidth: true },
                  }}
                />
                <DatePicker
                  label="To"
                  value={dateRange[1]}
                  minDate={dateRange[0] || moment().startOf('day')}
                  onChange={(v) => {
                    if (v && dateRange[0] && !v.isBefore(dateRange[0], 'day')) {
                      setDateRange([dateRange[0], v]);
                    }
                  }}
                  slotProps={{
                    ...drawerOverlays.datePickerSlotProps,
                    textField: { size: 'small', fullWidth: true },
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <Typography variant="subtitle2" gutterBottom>
                Day Selection
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', pl: 1 }}>
                {[
                  { value: 'custom', label: 'Custom' },
                  { value: 'weekdays', label: 'Weekdays' },
                  { value: 'weekend', label: 'Weekend' },
                ].map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    control={
                      <Radio
                        size="small"
                        checked={daySelectionType === opt.value}
                        onChange={() => selectDayType(opt.value)}
                        sx={{ color: alpha(THEME_COLOR, 0.6), '&.Mui-checked': { color: THEME_COLOR } }}
                      />
                    }
                    label={<Typography variant="body2">{opt.label}</Typography>}
                  />
                ))}
              </Box>
              {daySelectionType === 'custom' && (
                <Box sx={{ mt: 1.5, pl: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {DAY_OPTIONS.map((day) => {
                    const on = selectedDays.includes(day.value);
                    return (
                      <Chip
                        key={day.value}
                        label={`${day.shortLabel} ${day.label}`}
                        onClick={() => toggleDay(day.value)}
                        variant={on ? 'filled' : 'outlined'}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: on ? day.color : 'transparent',
                          color: on ? '#fff' : themeColors.subtleText,
                          borderColor: on ? day.color : themeColors.surfaceBorder,
                          fontWeight: on ? 600 : 400,
                        }}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          </LocalizationProvider>

          <Box sx={{ mb: 2.5 }}>
            <AllChannelsSelector
              channels={channels}
              value={selectedChannels}
              onChange={setSelectedChannels}
              allowPms={false}
              pmsMode="mirror"
              otaMode="display"
              themeColor={THEME_COLOR}
              disabled={isSaving}
              helperText="All Channels selects every OTA. Individual channels cannot be toggled."
            />
          </Box>

          <Box
            sx={{
              mb: 1,
              borderRadius: 1.5,
              border: `1px solid ${themeColors.surfaceBorder}`,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '40px 1fr',
                gap: 1,
                px: 1.25,
                py: 1,
                alignItems: 'center',
                backgroundColor: isDark ? alpha(THEME_COLOR, 0.12) : alpha(THEME_COLOR, 0.1),
                borderBottom: `1px solid ${themeColors.surfaceBorder}`,
              }}
            >
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={() =>
                  setSelectedKeys(
                    allSelected ? new Set() : new Set(planRows.map((r) => r.key))
                  )
                }
                sx={{
                  p: 0.5,
                  color: alpha(THEME_COLOR, 0.6),
                  '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: THEME_COLOR },
                }}
              />
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                RATEPLANS ({selectedKeys.size}/{planRows.length})
              </Typography>
            </Box>
            {Array.from(rowsByRoom.entries()).map(([roomId, group]) => (
              <Box key={roomId}>
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                    borderBottom: `1px solid ${themeColors.surfaceBorder}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, color: THEME_COLOR, textTransform: 'uppercase' }}
                  >
                    {group.name}
                  </Typography>
                </Box>
                {group.rows.map((row) => {
                  const checked = selectedKeys.has(row.key);
                  return (
                    <Box
                      key={row.key}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '40px 1fr',
                        gap: 1,
                        px: 1.25,
                        py: 0.65,
                        alignItems: 'center',
                        borderBottom: `1px solid ${themeColors.surfaceBorder}`,
                        opacity: checked ? 1 : 0.55,
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={() => {
                          setSelectedKeys((prev) => {
                            const next = new Set(prev);
                            if (next.has(row.key)) next.delete(row.key);
                            else next.add(row.key);
                            return next;
                          });
                        }}
                        sx={{
                          p: 0.5,
                          color: alpha(THEME_COLOR, 0.6),
                          '&.Mui-checked': { color: THEME_COLOR },
                        }}
                      />
                      <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                        {row.ratePlanName}
                        {row.mealPlan ? (
                          <Box component="span" sx={{ color: 'text.secondary' }}>
                            {' '}
                            ({row.mealPlan})
                          </Box>
                        ) : null}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            ))}
            {planRows.length === 0 && (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {mappingsLoading
                    ? 'Loading mapped Channel Manager rate plans…'
                    : 'No mapped Channel Manager room types and rate plans. Save channel mappings first.'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            display: 'flex',
            width: '100%',
            gap: 1,
            ...getBookingDrawerFooterSx(isDark),
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isSaving}
            sx={{ flex: 1, textTransform: 'none', ...getBookingDrawerOutlinedButtonSx(isDark) }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSync}
            disabled={isSaving || selectedKeys.size === 0}
            startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
            sx={{ flex: 1, textTransform: 'none', ...getBookingDrawerPrimaryButtonSx(isDark) }}
          >
            {isSaving ? 'Syncing…' : "Sync to OTA's"}
          </Button>
        </Box>
        <RateActionSuccessDialog
          open={Boolean(successDialog)}
          title={successDialog?.title}
          message={successDialog?.message}
          container={() => drawerRootRef.current || undefined}
          onClose={() => {
            setSuccessDialog(null);
            onClose();
          }}
        />
      </Box>
    </Drawer>
  );
}
